import React, {useCallback, useMemo, useState} from "react";
import { Grid } from '@mui/material'
import moment from 'moment'
import isEqual from 'react-fast-compare';
import SortFilter from '../../SortFilter/SortFilter';
import { sortByFields } from './data'
import useAllLocationNames from "../../../hooks/components/location/useAllLocationNames";
import { useBuyersBySupplier } from "../../../hooks/components/location/useBuyersBySupplier";
import type {SupplierAccountsReceivableRequestType} from "../../../types/reports/SupplierAccountsReceivableRequestType";
import { DateFormats } from '../../constants/securspace-constants'
import {useSnackbar} from "../../../context/snackbar-context";
import type {FilterInputProps, FilterBySelectProps, SortFilterCallback} from '../../SortFilter/interfaces';

interface SupplierAccountsReceivableFilterProps {
  filterCallback: (query: SupplierAccountsReceivableRequestType) => void;
  initialQuery: SupplierAccountsReceivableRequestType;
}

const SupplierAccountsReceivableFilter = React.memo(({
  filterCallback,
  initialQuery,
}: SupplierAccountsReceivableFilterProps) => {

  const { snackbarShowMessage } = useSnackbar();
  const locations = useAllLocationNames(snackbarShowMessage);
  const buyers = useBuyersBySupplier(snackbarShowMessage);
  const [latestQuery, setLatestQuery] = useState<SupplierAccountsReceivableRequestType | Record<string, never>>({});

  const handleCallback = useCallback((e: SortFilterCallback) => {
    const curatedQuery: SupplierAccountsReceivableRequestType = {
      // Default filters
      endDate: moment(e.endDate).format(DateFormats.DAY),
      startDate: moment(e.startDate).format(DateFormats.DAY),
      locationId: e.location?.id || '',
      // Filters
      bookingNumber: e.filters.find(filter => filter.name === 'bookingNumber')?.value || '',
      transactionNumber: e.filters.find(filter => filter.name === 'transactionNumber')?.value || '',
      isUnpaidOnly: Boolean(e.filters.find(filter => filter.name === 'isUnpaidOnly')?.value),
      // Select Filters
      buyerId: '',
      buyerAccountId: e.selectFilters?.find(filter => filter.name === 'buyerAccountId')?.value || '',
      // Sort
      sortBy: e.sortBy,
      sortDir: e.sortDir || 'desc',
    }

    // Only trigger callback if query has changed to prevent unnecessary API calls
    if (!isEqual(latestQuery, curatedQuery)) {
      setLatestQuery(curatedQuery);
      filterCallback(curatedQuery);
    }
  }, [filterCallback, latestQuery, setLatestQuery]);

  const filterByOptions: FilterInputProps[] = useMemo(() => {
    return [
      {
        label: "Booking Number",
        value: "",
        name: "bookingNumber",
        numberField: true,
        inputProps: { inputMode: "numeric" }
      },
      {
        label: "Invoice Number",
        value: initialQuery.transactionNumber || "",
        name: "transactionNumber",
        numberField: true,
        inputProps: { inputMode: "numeric" }
      },
      {
        label: "Unpaid Only",
        value: "true",
        name: "isUnpaidOnly",
        type: "checkbox" as const,
        disabled: true
      }
    ];
  }, [initialQuery]);

  const filterBySelectOptions: FilterBySelectProps[] = useMemo(() => {
    return [
      {
        label: 'Buyer', value: '', name: 'buyerAccountId',
        option: [
          ...buyers
        ]
      }
    ];
  }, [buyers]);

  return (
    <Grid container mb={4} flexDirection={'column'}>
      <SortFilter
        filterCallback={handleCallback}
        sortByOption={sortByFields}
        defaultEndDate={initialQuery.endDate}
        defaultStartDate={initialQuery.startDate}
        filterBySelectOption={filterBySelectOptions}
        locations={locations}
        defaultSortBy={initialQuery.sortBy}
        sortDir={initialQuery.sortDir as 'asc' | 'desc'}
        filterByOption={
          filterByOptions.map(filter => {
            const initialValue = initialQuery[filter.name as keyof SupplierAccountsReceivableRequestType];
            return {
              ...filter,
              value: typeof initialValue === 'string' || typeof initialValue === 'boolean'
                ? String(initialValue)
                : filter.value
            };
          })
        }
      />
    </Grid>
  )
});

SupplierAccountsReceivableFilter.displayName = 'SupplierAccountsReceivableFilter';

export default SupplierAccountsReceivableFilter;
